// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react';
import { Box, Link, Typography } from '@mui/material';
import { Block, InlineNode, tokenizeInline, tokenizeMarkdown } from './markdown';

/**
 * Render Khora's answer text as readable, SAFE markdown. It walks the shared markdown token tree (see
 * ./markdown) and emits REACT ELEMENTS — never raw HTML and never dangerouslySetInnerHTML — so it keeps
 * the same escaped-text XSS guarantee as plain <Typography>{text}</Typography>. The static-HTML export
 * (chatExport) walks the same tree, so the two views can't drift.
 */

// Only http(s)/mailto links are rendered as anchors; anything else (javascript:, data:, …) renders
// as plain text so a crafted link in the model output can't execute.
const safeHref = (url: string): string | null => {
  try {
    const scheme = new URL(url, window.location.origin).protocol.toLowerCase();
    return scheme === 'http:' || scheme === 'https:' || scheme === 'mailto:' ? url : null;
  } catch {
    return null;
  }
};

// Inline token nodes → React (text is auto-escaped by React). keyPrefix keeps sibling keys unique.
const renderInline = (nodes: InlineNode[], keyPrefix: string): React.ReactNode[] => nodes.map((node, index) => {
  const key = `${keyPrefix}-${index}`;
  if (node.type === 'text') return <React.Fragment key={key}>{node.value}</React.Fragment>;
  if (node.type === 'code') {
    return <Box key={key} component="code" sx={{ px: 0.5, borderRadius: 0.5, bgcolor: 'rgba(0,0,0,0.08)', fontFamily: 'monospace', fontSize: '0.85em' }}>{node.value}</Box>;
  }
  if (node.type === 'bold') return <strong key={key}>{renderInline(node.children, key)}</strong>;
  if (node.type === 'italic') return <em key={key}>{renderInline(node.children, key)}</em>;
  // link
  const href = safeHref(node.href);
  return href
    ? <Link key={key} href={href} target="_blank" rel="noopener noreferrer">{node.value}</Link>
    : <span key={key}>{node.value}</span>;
});

const HEADER_SIZES: Record<number, 'subtitle1' | 'subtitle2' | 'body2'> = { 1: 'subtitle1', 2: 'subtitle1', 3: 'subtitle2' };

// One block token → a React element.
const renderBlock = (block: Block, key: string): React.ReactNode => {
  switch (block.type) {
  case 'code':
    return (
      <Box key={key} component="pre" sx={{ my: 0.5, p: 1, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.08)', overflowX: 'auto', fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre' }}>
        {block.text}
      </Box>
    );
  case 'table':
    return (
      <Box
        key={key}
        sx={(theme) => ({ overflowX: 'auto', my: 0.75, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 })}
      >
        <Box
          component="table"
          sx={(theme) => ({
            borderCollapse: 'collapse',
            width: '100%',
            fontSize: '0.78rem',
            // Horizontal row lines only (no full grid) reads cleaner; brand-tinted header + zebra rows.
            '& th, & td': { px: 1, py: 0.5, textAlign: 'left', whiteSpace: 'nowrap', borderBottom: `1px solid ${theme.palette.divider}` },
            '& thead th': { fontWeight: 700, backgroundColor: 'rgba(1, 161, 221, 0.10)', borderBottom: `2px solid ${theme.palette.divider}` },
            '& tbody tr:nth-of-type(even)': { backgroundColor: theme.palette.action.hover },
            '& tbody tr:last-of-type td': { borderBottom: 'none' },
            '& tbody td:first-of-type': { fontWeight: 600 },
          })}
        >
          <thead><tr>{block.header.map((cell, ci) => <th key={ci}>{renderInline(tokenizeInline(cell), `${key}-th${ci}`)}</th>)}</tr></thead>
          <tbody>{block.rows.map((row, ri) => <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{renderInline(tokenizeInline(cell), `${key}-td${ri}-${ci}`)}</td>)}</tr>)}</tbody>
        </Box>
      </Box>
    );
  case 'heading':
    return (
      <Typography key={key} variant={HEADER_SIZES[block.level] ?? 'body2'} sx={{ fontWeight: 700, mt: 0.75, mb: 0.25 }}>
        {renderInline(tokenizeInline(block.text), `${key}-h`)}
      </Typography>
    );
  case 'hr':
    return <Box key={key} sx={{ my: 0.75, borderTop: '1px solid rgba(0,0,0,0.15)' }} />;
  case 'blockquote':
    return (
      <Box key={key} sx={{ borderLeft: '3px solid rgba(0,0,0,0.2)', pl: 1, my: 0.5, color: 'text.secondary' }}>
        <Typography variant="body2">{renderInline(tokenizeInline(block.text), `${key}-q`)}</Typography>
      </Box>
    );
  case 'list':
    return (
      <Box key={key} component={block.ordered ? 'ol' : 'ul'} sx={{ my: 0.25, pl: 2.5 }}>
        {block.items.map((item, ii) => (
          <Box key={ii} component="li" sx={{ mb: 0.25 }}>
            <Typography variant="body2" component="span">{renderInline(tokenizeInline(item), `${key}-li${ii}`)}</Typography>
          </Box>
        ))}
      </Box>
    );
  default: // paragraph
    return (
      <Typography key={key} variant="body2" sx={{ mb: 0.5, whiteSpace: 'pre-wrap' }}>
        {renderInline(tokenizeInline(block.text), `${key}-p`)}
      </Typography>
    );
  }
};

const ChatMarkdown: React.FC<{ text: string }> = ({ text }) => (
  <Box sx={{ '& > *:first-of-type': { mt: 0 }, '& > *:last-child': { mb: 0 } }}>
    {tokenizeMarkdown(text).map((block, index) => renderBlock(block, `b${index}`))}
  </Box>
);

export default ChatMarkdown;
